import esriConfig from '@arcgis/core/config';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Extent from '@arcgis/core/geometry/Extent';
import Geometry from '@arcgis/core/geometry/Geometry';
import * as geometryEngineAsync from '@arcgis/core/geometry/geometryEngineAsync';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as projection from '@arcgis/core/geometry/projection';
import GeographicTransformation from '@arcgis/core/geometry/support/GeographicTransformation';
import Graphic from '@arcgis/core/Graphic';
import Credential from '@arcgis/core/identity/Credential';
import esriId from '@arcgis/core/identity/IdentityManager';
import OAuthInfo from '@arcgis/core/identity/OAuthInfo';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import Field from '@arcgis/core/layers/support/Field';
import Map from '@arcgis/core/Map';
import Portal from '@arcgis/core/portal/Portal';
import PortalItem from '@arcgis/core/portal/PortalItem';
import PortalQueryParams from '@arcgis/core/portal/PortalQueryParams';
import PortalQueryResult from '@arcgis/core/portal/PortalQueryResult';
import esriRequest from '@arcgis/core/request';
import { executeQueryJSON } from '@arcgis/core/rest/query';
import FeatureSet from '@arcgis/core/rest/support/FeatureSet';
import Query from '@arcgis/core/rest/support/Query';
import * as colorRendererCreator from '@arcgis/core/smartMapping/renderers/color';
import * as sizeRendererCreator from '@arcgis/core/smartMapping/renderers/size';
import * as typeRendererCreator from '@arcgis/core/smartMapping/renderers/type';
import summaryStatistics from '@arcgis/core/smartMapping/statistics/summaryStatistics';
import * as sizeSchemes from '@arcgis/core/smartMapping/symbology/size';
import * as typeSchemes from '@arcgis/core/smartMapping/symbology/type';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import FeatureTable from '@arcgis/core/widgets/FeatureTable';

/*
react example: https://github.com/Esri/jsapi-resources/tree/master/esm-samples/jsapi-create-react-app
*/

/*
Using on-premise.

- Set the hostname to the on-premise portal

https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#portalUrl

esriConfig.portalUrl = "https://myHostName.esri.com/arcgis"

default Value:"https://www.arcgis.com"
*/

/*
Using on-premise with no external resources.

- Service URL required if you want to use Search Widgets or any other service.
- How do we fetch appconfig? Its an external URL.
*/

/*
Upgrading from esri-loader

https://developers.arcgis.com/javascript/latest/es-modules/

--- ERRORS ---

"You may need an additional loader to handle the result of these loaders."

If you run into issues with babel helpers injected into ArcGIS API for JavaScript 
code during the build using create-react-app, but helpers not being copied to the build, 
you can try using the following browserslist configuration.

https://github.com/facebook/create-react-app/issues/9468

Change browser targets in package.json from

 "browserslist": {
   "production": [
      ">0.2%",
      "not dead",
      "not op_mini all"
    ],
    "development": [
      "last 1 chrome version",
      "last 1 firefox version",
      "last 1 safari version"
    ]
},

to

"browserslist": [
   ">0.2%",
  "not dead",
  "not op_mini all"
],

see also: https://github.com/Esri/jsapi-resources/tree/master/esm-samples/jsapi-create-react-app

--- MANAGING ASSETS LOCALLY ---

// https://developers.arcgis.com/javascript/latest/es-modules/#managing-assets-locally

> npm install ncp
> npm install @arcgis/core

--- package.json --- 
Add "copy-arcgis-assets"
Copies assets to public folder on npm start or build.
Remove esri-loader from package.json and any other references.

"start": "npm run copy-arcgis-assets && react-scripts start",
"build": "npm run copy-arcgis-assets && node ./scripts/build-js-bundle.js",
"postbuild": "node ./scripts/build-css-bundle.js",
"test": "react-scripts test",
"eject": "react-scripts eject",
"copy-arcgis-assets": "ncp ./node_modules/@arcgis/core/assets ./public/assets"

--- .gitignore --- 
We dont want to add all those assets to the repo.
# arcgis assets
/public/assets

--- App.tsx or js-api-util.ts ---
Set the assets path.
esriConfig.assetsPath = `${process.env.PUBLIC_URL}/assets`;

--- index.css ---
Import css.
@import "@arcgis/core/assets/esri/themes/light/main.css";
or...
@import "@arcgis/core/assets/esri/themes/dark/main.css";
*/

// https://developers.arcgis.com/javascript/latest/es-modules/#managing-assets-locally
//esriConfig.assetsPath = `${process.env.PUBLIC_URL}/assets`;

// Set the portalUrl to the on-premise portal if using one.
// https://developers.arcgis.com/javascript/latest/api-reference/esri-config.html#portalUrl
//esriConfig.portalUrl = 'https://www.arcgis.com';

/*
 * Clean up common issues with user entered portal URLs.
 */
export const fixUrl = (portalUrl: string = ''): string => {
    let url = 'https://www.arcgis.com';
    if (portalUrl === '' && esriConfig.portalUrl !== undefined)
        // Default to esriConfig.portalUrl - default being https://www.arcgis.com.
        url = esriConfig.portalUrl;

    if (portalUrl.search('/home/') > 0)
        // Strip the /home endpoint.
        url = `${portalUrl.substr(0, portalUrl.search('/home'))}`;
    else if (portalUrl.search('/sharing') > 0)
        // Strip the /sharing endpoint.
        url = `${portalUrl.substr(0, portalUrl.search('/sharing'))}`;

    if (
        url.indexOf('http://') === 0 &&
        window.location.href.indexOf('https://') === 0
    )
        // Upgrade the URL to https.
        url = url.replace('http://', 'https://');
    return url;
};

// Use interceptors to pass token so we can use
// the JavaScript API rather than the REST API
// when using layers stored in the quickquote AGOL account
// whilst the user is signed in to a different account.
export const interceptRequests = ({
    items,
    token,
    portalUrl
}: {
    items: any[];
    token: string;
    portalUrl?: string;
}) => {
    const url: string = fixUrl(portalUrl);
    const interceptors: any[] = [];
    for (let item of items) {
        // Item and layer urls.
        interceptors.push({
            urls: [
                `${url}/sharing/rest/content/items/${item.id}`,
                item.url,
                `${item.url}/0`,
                `${item.url}/0/query`
            ],
            before: (params: any) => {
                params.requestOptions.query = params.requestOptions.query || {};
                params.requestOptions.query.token = token;
            }
        });
    }

    if (esriConfig.request.interceptors !== undefined)
        esriConfig.request.interceptors = [
            ...interceptors,
            ...esriConfig.request.interceptors
        ];
    else esriConfig.request.interceptors = [...interceptors];
};

// Identity Manager.
export const registerAppWithOAuth = (
    appId: string,
    portalUrl?: string
): OAuthInfo => {
    const url: string = fixUrl(portalUrl);
    const info = new OAuthInfo({
        appId,
        // Uncomment the next line and update if using your own portal
        portalUrl: url,
        // Uncomment the next line to prevent the user's signed
        // in state from being shared with other apps on the same
        // domain with the same authNamespace value.
        // authNamespace: "portal_oauth_inline",
        popup: false
    });
    esriId.registerOAuthInfos([info]);
    return info;
};

export const registerToken = ({
    token,
    server
}: {
    token: string;
    server?: string;
}) => {
    const url: string = fixUrl(server);
    esriId.registerToken({
        token,
        server: `${url}/sharing/rest`
    });
};

export const checkSignInStatus = async ({
    portalUrl
}: {
    portalUrl?: string;
} = {}): Promise<Credential | undefined> => {
    try {
        const url: string = fixUrl(portalUrl);
        const credential = await esriId.checkSignInStatus(`${url}/sharing`);
        return credential; // logged in.
    } catch (error) {
        return undefined; // logged out.
    }
};

export const signIn = async ({
    portalUrl,
    ...rest
}: {
    portalUrl?: string;
} = {}): Promise<Credential | undefined> => {
    const url: string = fixUrl(portalUrl);
    const credential = await esriId.getCredential(`${url}/sharing`, {
        ...rest
    });
    return credential;
};

export const signOut = () => {
    sessionStorage.clear();
    destroyCredentials();
    window.location.reload();
};

export const destroyCredentials = () => {
    esriId.destroyCredentials();
};

export const identityToJson = (): any => {
    return esriId.toJSON();
};

export const initializeIdentity = (json: any) => {
    esriId.initialize(json);
};

export const findCredential = (
    portalUrl?: string,
    userId?: string
): Credential => {
    const url: string = fixUrl(portalUrl);
    return esriId.findCredential(url, userId);
};

// Portal
export const loadPortal = async ({
    portalUrl,
    ...rest
}: {
    portalUrl?: string;
} = {}): Promise<Portal> => {
    const url: string = fixUrl(portalUrl);
    const portal = new Portal({ url, ...rest });
    // Setting authMode to immediate signs the user in once loaded.
    portal.authMode = 'immediate';
    // Once loaded, user is signed in.
    await portal.load();
    return portal;
};

export const loadPortalItem = async ({
    id,
    portalUrl,
    ...rest
}: {
    id: string;
    portalUrl?: string;
}): Promise<PortalItem> => {
    const url: string = fixUrl(portalUrl);
    const item = new PortalItem({ id, portal: { url }, ...rest });
    await item.load();
    return item;
};

export const queryPortal = async ({
    portal,
    ...rest
}: {
    portal: Portal;
}): Promise<PortalQueryResult> => {
    const queryParams = new PortalQueryParams({ ...rest });
    return await portal.queryItems(queryParams);
};

export const queryPortalItems = async ({
    portal,
    searchTerm,
    scope,
    types = ['Feature Service'],
    ...rest
}: {
    portal: Portal;
    searchTerm: string;
    scope: string;
    types?: string[];
}): Promise<PortalQueryResult> => {
    let query = '';
    if (types.length > 0)
        query += `type:(${types
            .map((type: string) => `"${type}"`)
            .join(' OR ')})`;
    if (searchTerm !== '') query += ` AND (${searchTerm})`;
    if (scope === 'user') query += ` AND owner:"${portal.user.username}"`;
    if (scope === 'org') query += ` AND accountid:"${portal.user.orgId}"`;
    const response = await queryPortal({
        portal,
        query,
        ...rest
    });
    return response;
};

export const createFeatureTable = ({
    container,
    ...rest
}: {
    container: string;
}): FeatureTable => {
    return new FeatureTable({ container, ...rest });
};

export const createMapView = ({
    container,
    map,
    ...rest
}: {
    container: string;
    map: Map;
}): MapView => {
    return new MapView({ container, map, ...rest });
};

export const createMap = ({
    basemapId,
    basemap = 'gray-vector',
    portalUrl,
    ...rest
}: {
    basemapId?: string;
    basemap?: string;
    portalUrl?: string;
} = {}): Map => {
    if (basemapId !== undefined) {
        const url: string = fixUrl(portalUrl);
        return new Map({
            basemap: {
                portalItem: {
                    id: basemapId,
                    portal: { url }
                }
            },
            ...rest
        });
    } else return new Map({ basemap, ...rest });
};

export const createWebMap = ({
    id,
    portalUrl,
    ...rest
}: {
    id: string;
    portalUrl?: string;
}): WebMap => {
    const url: string = fixUrl(portalUrl);
    return new WebMap({
        portalItem: {
            id,
            portal: { url }
        },
        ...rest
    });
};

export const createLayerFromPortalItem = async ({
    itemId,
    portalUrl,
    ...rest
}: {
    itemId: string;
    portalUrl?: string;
}): Promise<Layer> => {
    const url: string = fixUrl(portalUrl);
    return await Layer.fromPortalItem({
        portalItem: {
            id: itemId,
            portal: { url }
        } as PortalItem,
        ...rest
    });
};

export const createFeatureLayerFromPortalItem = ({
    itemId,
    layerId = 0,
    portalUrl,
    ...rest
}: {
    itemId: string;
    layerId?: number;
    portalUrl?: string;
}): FeatureLayer => {
    const url: string = fixUrl(portalUrl);
    return new FeatureLayer({
        portalItem: { id: itemId, portal: { url } },
        layerId,
        ...rest
    });
};

export const createGroupLayer = ({ ...rest }): GroupLayer => {
    return new GroupLayer({ ...rest });
};

export const createFeatureLayer = ({
    source,
    url,
    color,
    fillColor = [51, 136, 255, 0.3],
    fillOpacity = 0.3,
    outlineColor = [51, 136, 255],
    outlineOpacity = 1,
    outlineWidth = 1,
    ...rest
}: {
    source?: __esri.CollectionProperties<__esri.GraphicProperties>;
    url?: string;
    color?: number[];
    fillColor?: number[];
    fillOpacity?: number;
    outlineColor?: number[];
    outlineOpacity?: number;
    outlineWidth?: number;
}): FeatureLayer => {
    return new FeatureLayer({
        source,
        objectIdField: source !== undefined ? 'OBJECTID' : undefined,
        url,
        renderer: {
            type: 'simple',
            symbol: {
                type: 'simple-fill',
                color:
                    color !== undefined
                        ? [...color, fillOpacity]
                        : [...fillColor],
                outline: {
                    color:
                        color !== undefined
                            ? [...color, outlineOpacity]
                            : [...outlineColor],
                    width: outlineWidth
                }
            }
        } as __esri.RendererProperties,
        ...rest
    });
};

export const createGraphicsLayer = ({
    graphics = [],
    ...rest
}: {
    graphics?: Graphic[];
} = {}): GraphicsLayer => {
    return new GraphicsLayer({ graphics, ...rest });
};

export const createGraphicsLayerFromGeometries = ({
    geometries = [],
    color,
    fillColor = [51, 136, 255, 0.3],
    fillOpacity = 0.3,
    outlineColor = [51, 136, 255],
    outlineOpacity = 1,
    outlineWidth = 1,
    ...rest
}: {
    geometries: Geometry[];
    color?: number[];
    fillColor?: number[];
    fillOpacity?: number;
    outlineColor?: number[];
    outlineOpacity?: number;
    outlineWidth?: number;
}): GraphicsLayer => {
    const graphics: Graphic[] = getGraphicsForGeometries({
        geometries,
        color,
        fillColor,
        fillOpacity,
        outlineColor,
        outlineOpacity,
        outlineWidth
    });
    return new GraphicsLayer({ graphics, ...rest });
};

export const getGeometryExtents = ({
    geometries = []
}: {
    geometries: Geometry[];
}): Extent => {
    let extent = new Extent();
    for (const [index, geom] of geometries.entries()) {
        if (index === 0) extent = geom.extent.clone();
        else extent.union(geom.extent);
    }
    return extent;
};

export const loadProjection = async (): Promise<void> => {
    return await projection.load();
};

// Runs queryFeatures()
// Accounts for  maxRecordCount to make sure all features are included in the query.
// If youre using start and num you need to use it for every query otherwise
// the same features get fetched again.
// So we need to know maxRecordCount before the query to set the num. We can get that
// from the capabilities property of the layer but thats only available if the layer has been loaded.
// So we need to get the layer info first - which is a pain because it adds an extra request.
export const queryFeatureLayer = async ({
    layer,
    onFeaturesReturned = () => {}, // Use for large datasets when you dont want to wait for all features to be loaded.
    f = 'json',
    outFields = ['*'],
    where = '1=1',
    returnGeometry = false,
    ...rest
}: {
    layer: FeatureLayer;
    onFeaturesReturned?: Function;
    f?: string;
    outFields?: string[];
    where?: string;
    returnGeometry?: boolean;
}): Promise<FeatureSet> => {
    let features: Graphic[] = [];
    let exceededTransferLimit: boolean = true;
    let featureSet = new FeatureSet();
    let num: number = layer?.capabilities?.query?.maxRecordCount;
    if (num === undefined || num === null) {
        const info = await esriRequest(layer.url, { query: { f: 'json' } });
        num = info?.data?.maxRecordCount;
    }
    if (num === undefined || num === null || num < 0) num = 1000;
    let start = 0;
    while (exceededTransferLimit === true) {
        try {
            featureSet = await layer.queryFeatures({
                start,
                num,
                f,
                outFields,
                where,
                returnGeometry,
                ...rest
            });

            // Use "any" instead of "FeatureSet" because featureSet.error throws linting error.
            // Property 'error' does not exist on type 'FeatureSet'.
            const fsError = featureSet as any;
            if (fsError.error !== undefined)
                return Promise.reject(new Error(fsError.error.message));

            onFeaturesReturned(featureSet);
            features =
                featureSet.features !== undefined
                    ? [...features, ...featureSet.features]
                    : [...features];
            start = features.length;
            exceededTransferLimit = featureSet.exceededTransferLimit;
        } catch (error) {
            return Promise.reject(error);
        }
    }
    featureSet.features = features;
    return featureSet;
    //return Object.assign(featureSet, { features });
};

// Executes a query against a feature service.
// Accounts for  maxRecordCount to make sure all features are included in the query.
export const queryFeatureService = async ({
    url,
    onFeaturesReturned = () => {}, // Use for large datasets when you dont want to wait for all features to be loaded.
    f = 'json',
    outFields = ['*'],
    where = '1=1',
    returnGeometry = false,
    ...rest
}: {
    url: string;
    onFeaturesReturned?: Function;
    f?: string;
    outFields?: string[];
    where?: string;
    returnGeometry?: boolean;
}): Promise<FeatureSet> => {
    let features: Graphic[] = [];
    let exceededTransferLimit: boolean = true;
    let featureSet = new FeatureSet();
    while (exceededTransferLimit === true) {
        try {
            const query = new Query({
                f,
                outFields,
                where,
                returnGeometry,
                resultOffset: features.length,
                ...rest
            });
            // When resolved, returns features and graphics that satisfy the query.
            featureSet = await executeQueryJSON(url, query);

            // Use "any" instead of "FeatureSet" because featureSet.error throws linting error.
            // Property 'error' does not exist on type 'FeatureSet'.
            const fsError = featureSet as any;
            if (fsError.error !== undefined)
                return Promise.reject(new Error(fsError.error.message));

            onFeaturesReturned(featureSet);
            features =
                featureSet.features !== undefined
                    ? [...features, ...featureSet.features]
                    : [...features];
            exceededTransferLimit = featureSet.exceededTransferLimit;
        } catch (error) {
            return Promise.reject(error);
        }
    }
    featureSet.features = features;
    return featureSet;
    //return Object.assign(featureSet, { features });
};

export const createSmartRenderer = async ({
    view,
    layer,
    field,
    classificationMethod = 'high-to-low',
    size,
    minSize,
    maxSize,
    ...rest
}: {
    view: MapView;
    layer: FeatureLayer;
    field: string;
    classificationMethod?:
        | 'high-to-low'
        | 'above'
        | 'below'
        | 'above-and-below'
        | 'centered-on'
        | 'extremes'
        | 'equal-interval'
        | 'natural-breaks'
        | 'quantile'
        | 'standard-deviation'
        | 'proportional-circles';
    size?: number;
    minSize?: number;
    maxSize?: number;
}): Promise<
    | __esri.ContinuousRendererResult
    | __esri.sizeContinuousRendererResult
    | __esri.ClassBreaksRendererResult
    | __esri.typeRendererResult
    | null
> => {
    const fld = layer.fields.filter((f: Field) => f.name === field)[0];
    if (fld !== undefined) {
        const params = { layer, view, field, ...rest };
        // Numeric.
        if (
            fld.type === 'integer' ||
            fld.type === 'small-integer' ||
            fld.type === 'single' ||
            fld.type === 'double' ||
            fld.type === 'date'
        ) {
            if (classificationMethod === 'proportional-circles') {
                // Custom size defined.
                const schemes = sizeSchemes.getSchemes({
                    basemap: view.map.basemap,
                    geometryType: layer.geometryType as
                        | 'point'
                        | 'multipoint'
                        | 'polyline'
                        | 'polygon'
                });

                const sizeScheme: __esri.SizeSchemeForPoint =
                    sizeSchemes.cloneScheme(
                        schemes.primaryScheme
                    ) as __esri.SizeSchemeForPoint;

                if (minSize !== undefined) sizeScheme.minSize = minSize;
                if (maxSize !== undefined) sizeScheme.maxSize = maxSize;

                // proportional-circles.
                return await sizeRendererCreator.createContinuousRenderer({
                    sizeScheme,
                    ...params
                });
            } else {
                if (
                    classificationMethod === 'equal-interval' ||
                    classificationMethod === 'natural-breaks' ||
                    classificationMethod === 'quantile' ||
                    classificationMethod === 'standard-deviation'
                ) {
                    // "equal-interval"|"natural-breaks"|"quantile"|"standard-deviation".
                    return await colorRendererCreator.createClassBreaksRenderer(
                        {
                            classificationMethod,
                            ...params
                        }
                    );
                } else {
                    return await colorRendererCreator.createContinuousRenderer({
                        theme: classificationMethod,
                        ...params
                    });
                }
            }
        }
        // Categoric.
        else {
            // Custom size defined.
            const schemes = typeSchemes.getSchemes({
                basemap: view.map.basemap,
                geometryType: layer.geometryType
            });

            const typeScheme: __esri.TypeSchemeForPoint =
                typeSchemes.cloneScheme(
                    schemes.primaryScheme
                ) as __esri.TypeSchemeForPoint;

            if (size !== undefined) typeScheme.size = size;

            return await typeRendererCreator.createRenderer({
                numTypes: -1,
                typeScheme,
                ...params
            });
        }
    } else return null;
};

// Zooms to extent accounting for projection.
export const zoomToExtent = async ({
    view,
    extent
}: {
    view: MapView;
    extent: Extent;
}): Promise<any> => {
    if (projection.isLoaded() === false) await loadProjection();
    const projExtent: Geometry | Geometry[] = projection.project(extent, {
        wkid: view.spatialReference.wkid
    });
    return await view.goTo(projExtent);
};

// Zooms to features.
export const zoomToFeatures = async ({
    view,
    features
}: {
    view: MapView;
    features: Graphic[];
}): Promise<any> => {
    return await view.goTo(features);
};

// Layer extent in view spatial reference.
export const layerExtent = async ({
    view,
    layer,
    where = '1=1',
    ...rest
}: {
    view: MapView;
    layer: FeatureLayer;
    where?: string;
}): Promise<any> => {
    return await layer.queryExtent({
        where,
        outSpatialReference: view.spatialReference,
        ...rest
    });
};

// Zooms to layer.
export const zoomToLayer = async ({
    view,
    layer,
    where = '1=1',
    ...rest
}: {
    view: MapView;
    layer: FeatureLayer;
    where?: string;
}): Promise<any> => {
    const response: any = await layerExtent({ view, layer, where, ...rest });
    return await view.goTo(response.extent);
};

// Calls the passed in function when the view is stationary.
// Useful for getting the info about the current view like its extents.
export const onViewStationary = ({
    view,
    onChange = () => {}
}: {
    view: MapView;
    onChange?: Function;
}) => {
    view.when(function () {
        reactiveUtils.when(
            () => view.stationary === true,
            function () {
                onChange(true);
            }
        );
        reactiveUtils.when(
            () => view.stationary === false,
            function () {
                onChange(false);
            }
        );
    });
};

// Calls the passed in function when the view is ready.
// This includes all layers and basemaps.
export const onViewReady = ({
    view,
    onReady = () => {}
}: {
    view: MapView;
    onReady?: Function;
}) => {
    view.when(function () {
        reactiveUtils
            .whenOnce(() => !view.updating)
            .then(() => {
                onReady(true);
            });
    });
};

// Returns the geometry for a FeatureLayer or GraphicsLayer
// and projects to the given wkid.
export const getLayerGeometry = async ({
    layer,
    wkid,
    ...rest
}: {
    layer: Layer;
    wkid?: number;
}): Promise<Geometry | Geometry[]> => {
    const isFeatureLayer = layer instanceof FeatureLayer;
    const isGraphicsLayer = layer instanceof GraphicsLayer;
    if (isFeatureLayer || isGraphicsLayer) {
        let arrGeoms: Geometry | Geometry[] = [];
        let inWKID: number | undefined = undefined;
        let isGeographic: boolean = false;
        let isFeatureCollection: boolean = false;
        let isFeatureService: boolean = false;

        if (isFeatureLayer) {
            const lyr = layer as FeatureLayer;
            inWKID = lyr.spatialReference.wkid;
            isGeographic = lyr.spatialReference.isGeographic;
            isFeatureService = lyr.url !== undefined && lyr.url !== null;
            isFeatureCollection = lyr.source.length > 0;
        }
        if (isGraphicsLayer) {
            const lyr = layer as GraphicsLayer;
            if (lyr.graphics.length > 0) {
                const spatialReference =
                    lyr.graphics.getItemAt(0).geometry.spatialReference;
                inWKID = spatialReference.wkid;
                isGeographic = spatialReference.isGeographic;
            }
        }
        const doProjection: boolean = wkid !== undefined && wkid !== inWKID;

        // Feature Service or Feature Collection or GraphicsLayer.
        if (isFeatureService === true) {
            // Handle Feature Service.
            const featureSet: FeatureSet = await queryFeatureLayer({
                layer: layer as FeatureLayer,
                returnGeometry: true,
                outSpatialReference:
                    doProjection === true && isGeographic !== true
                        ? { wkid }
                        : { wkid: inWKID },
                ...rest
            });
            arrGeoms = featureSet.features.map((f: Graphic) => f.geometry);
        } else if (isFeatureCollection === true) {
            const lyr = layer as FeatureLayer;
            arrGeoms = lyr.source.map((f: Graphic) => f.geometry).toArray();
        } else if (isGraphicsLayer === true) {
            const lyr = layer as GraphicsLayer;
            arrGeoms = lyr.graphics.map((f: Graphic) => f.geometry).toArray();
        }

        if (arrGeoms.length > 0) {
            if (
                inWKID !== undefined &&
                doProjection === true &&
                (isGeographic === true || isFeatureCollection === true)
            ) {
                let extent;
                if (isGraphicsLayer === true)
                    extent = await getGeometryExtents({ geometries: arrGeoms });
                else {
                    const lyr = layer as FeatureLayer;
                    const response = await lyr.queryExtent({ where: '1=1' });
                    extent = response.extent;
                }
                // A transformation needs to be applied
                // for a geographic spatial reference.
                arrGeoms = await projectGeometries({
                    isGeographic,
                    geometries: arrGeoms,
                    extent,
                    inWKID,
                    outWKID: wkid
                });
            }
            return arrGeoms;
        } else return Promise.reject(new Error('No features found in layer'));
    } else
        return Promise.reject(
            new Error(`"${layer.type}" layers are not supported at this time`)
        );
};

export const getGraphicsForGeometries = ({
    geometries = [],
    color,
    fillColor = [51, 136, 255, 0],
    fillOpacity = 0,
    outlineColor = [51, 136, 255],
    outlineOpacity = 1,
    outlineWidth = 1,
    ...rest
}: {
    geometries: Geometry[];
    color?: number[];
    fillColor?: number[];
    fillOpacity?: number;
    outlineColor?: number[];
    outlineOpacity?: number;
    outlineWidth?: number;
}): Graphic[] => {
    const graphics: Graphic[] = geometries.map((geometry: Geometry) => {
        const fillSymbol = {
            type: geometry.type === 'point' ? 'simple-marker' : 'simple-fill',
            color:
                color !== undefined ? [...color, fillOpacity] : [...fillColor],
            outline: {
                color:
                    color !== undefined
                        ? [...color, outlineOpacity]
                        : [...outlineColor],
                width: outlineWidth
            },
            ...rest
        };
        return new Graphic({
            geometry,
            symbol: fillSymbol
        });
    });
    return graphics;
};

// Project Geometries
export const projectGeometries = async ({
    isGeographic,
    geometries,
    extent,
    inWKID,
    outWKID
}: {
    isGeographic?: boolean;
    geometries: Geometry | Geometry[];
    extent: Extent;
    inWKID: number;
    outWKID?: number;
}): Promise<Geometry | Geometry[]> => {
    if (projection.isLoaded() === false) await loadProjection();
    if (isGeographic === true) {
        const arrGeoTrans: GeographicTransformation[] =
            projection.getTransformations(
                { wkid: inWKID },
                { wkid: outWKID },
                extent
            );
        return projection.project(
            geometries,
            { wkid: outWKID },
            arrGeoTrans.length > 0 ? arrGeoTrans[0] : undefined
        );
    } else return projection.project(geometries, { wkid: outWKID });
};

// Buffer geometries.
export const bufferGeometries = async ({
    geometries,
    distance = 100,
    units = 'meters',
    unionResults = true
}: {
    geometries: Geometry | Geometry[];
    distance?: number;
    units?: __esri.LinearUnits;
    unionResults?: boolean;
}): Promise<Polygon | Polygon[]> => {
    const geomBuffer = await geometryEngineAsync.buffer(
        geometries,
        distance,
        units,
        unionResults
    );
    return geomBuffer;
};

// Generalize a geometry without losing area.
export const generalizeGeometries = async ({
    geometries,
    distance = 100,
    units = 'meters'
}: {
    geometries: Geometry | Geometry[];
    distance?: number;
    units?: __esri.LinearUnits;
    unionResults?: boolean;
}): Promise<Geometry> => {
    const geomBuffer: Polygon = (await bufferGeometries({
        geometries,
        distance,
        units
    })) as Polygon;
    const geomGen: Geometry = await geometryEngineAsync.generalize(
        geomBuffer,
        distance
    );
    const geomSimplify: Geometry = await geometryEngineAsync.simplify(geomGen);
    return geomSimplify;
};

// Fixes field names if they are wrong case.
// This is used in conjunction with guessing field names.
export const fixFieldName = ({
    layer,
    fieldName,
    caseSensitive = false // case-insensitive, because ArcGIS Enterprise messes with case of field names.
}: {
    layer: FeatureLayer;
    fieldName: string;
    caseSensitive?: boolean;
}): string | undefined => {
    let arr: Field[] = [];
    if (layer !== undefined)
        arr = layer.fields.filter(
            (fld: Field) =>
                fld.name === fieldName ||
                (!caseSensitive &&
                    fld.name.toLowerCase() === fieldName.toLowerCase())
        );
    return arr.length > 0 ? arr[0].name : undefined;
};

// Checks if a field exists.
export const hasField = ({
    layer,
    fieldName
}: {
    layer: FeatureLayer;
    fieldName: string;
}): boolean => {
    let arr: Field[] = [];
    if (layer !== undefined)
        arr = layer.fields.filter((fld: Field) => fld.name === fieldName);
    return arr.length > 0 ? true : false;
};

// Gets a field.
export const getField = ({
    layer,
    fieldName
}: {
    layer: FeatureLayer;
    fieldName: string;
}): Field | undefined => {
    let arr: Field[] = [];
    if (layer !== undefined)
        arr = layer.fields.filter((fld: Field) => fld.name === fieldName);
    return arr.length > 0 ? arr[0] : undefined;
};

export const summaryStats = async ({
    layer,
    fieldName
}: {
    layer: FeatureLayer;
    fieldName: string;
}): Promise<__esri.SummaryStatisticsResult> => {
    return await summaryStatistics({ field: fieldName, layer });
};
